import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Star, MapPin, Clock, Shield, Award, ArrowLeft,
  Phone, MessageSquare, ChevronDown, ChevronUp, Loader2, CheckCircle, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER_AVATAR = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="%23e5e7eb"/><circle cx="40" cy="30" r="14" fill="%2393a3b8"/><path d="M16 70c0-13 11-22 24-22s24 9 24 22" fill="%2393a3b8"/></svg>';

// Normalise a professional doc to a UI-friendly shape so both legacy seed pros
// (flat string services + response_time/location/distance/review_count) and
// imported pros (services as objects, business_description, service_areas, …)
// render correctly. NEVER throws — every field has a safe default.
const normaliseProfessional = (p) => {
  const rawServices = Array.isArray(p?.services) ? p.services : [];
  const services = rawServices
    .map((s) => (typeof s === 'string' ? s : (s && s.service_id) || ''))
    .filter(Boolean);
  const firstSvc = rawServices.find((s) => s && typeof s === 'object') || null;

  const location =
    p?.location ||
    p?.service_areas ||
    [p?.city, p?.province].filter(Boolean).join(', ') ||
    '';

  return {
    id: p?.id || '',
    name: p?.name || 'Professional',
    owner_name: p?.owner_name || '',
    image: p?.image || p?.profile_image || PLACEHOLDER_AVATAR,
    verified: Boolean(p?.verified || p?.is_verified),
    top_pro: Boolean(p?.top_pro),
    rating: typeof p?.rating === 'number' ? p.rating : 5.0,
    review_count: typeof p?.review_count === 'number' ? p.review_count : 0,
    location,
    distance: p?.distance || '',
    response_time: p?.response_time || '',
    years_experience:
      p?.years_experience ||
      (firstSvc && firstSvc.years_experience) ||
      null,
    description: p?.description || p?.business_description || '',
    services,
    price_range: p?.price_range || 'RR',
  };
};

const ResultsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPro, setExpandedPro] = useState(null);
  const [contacted, setContacted] = useState(new Set());

  const serviceName = location.state?.service_name || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const contactInfo = location.state?.contact || {};
  const requestId = location.state?.request_id || null;

  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        const res = await axios.get(`${API}/professionals/match/${slug}`);
        const raw = Array.isArray(res.data) ? res.data : [];
        setProfessionals(raw.map(normaliseProfessional));
      } catch (e) {
        console.error('Error fetching professionals:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfessionals();
  }, [slug]);

  const handleContact = (proId) => {
    setContacted(prev => new Set([...prev, proId]));
  };

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={14}
            className={i < full ? 'text-[#FFB800] fill-[#FFB800]' : (i === full && half ? 'text-[#FFB800] fill-[#FFB800]/50' : 'text-gray-200 fill-gray-200')}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-[#37b34a] mx-auto mb-4" />
          <p className="text-gray-500">Finding the best professionals for you...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors flex-shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-900 truncate">{serviceName}</h1>
            <p className="text-sm text-gray-500">
              {professionals.length} professional{professionals.length !== 1 ? 's' : ''} found
              {contactInfo.location && ` near ${contactInfo.location}`}
            </p>
          </div>
          <a
            href="/"
            className="hidden sm:block text-sm text-[#37b34a] font-medium hover:underline"
          >
            New search
          </a>
        </div>
      </div>

      {/* Success Banner */}
      {requestId && (
        <div className="bg-[#37b34a]/5 border-b border-[#37b34a]/10">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
            <CheckCircle size={18} className="text-[#37b34a] flex-shrink-0" />
            <p className="text-sm text-gray-700">
              <strong>Your request has been submitted!</strong> These professionals will receive your details and get in touch.
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6">
        {professionals.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <User size={48} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">No professionals found yet</h3>
            <p className="text-gray-500 text-sm mb-6">
              We&apos;re still growing our network for this service. Try a different search or check back soon.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Browse services
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {professionals.map((pro, idx) => {
              const isExpanded = expandedPro === idx;
              const isContacted = contacted.has(pro.id);

              return (
                <div
                  key={pro.id}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  <div className="p-5 sm:p-6">
                    <div className="flex gap-4">
                      {/* Profile Photo */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden">
                          <img
                            src={pro.image}
                            alt={pro.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                              <span className="truncate">{pro.name}</span>
                              {pro.verified && (
                                <Shield size={14} className="text-blue-500 flex-shrink-0" />
                              )}
                            </h3>
                            <p className="text-sm text-gray-500 mt-0.5">{pro.owner_name}</p>
                          </div>
                          {pro.top_pro && (
                            <span className="flex-shrink-0 bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                              <Award size={12} />
                              Top Pro
                            </span>
                          )}
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2 mt-2">
                          {renderStars(pro.rating)}
                          <span className="text-sm font-semibold text-gray-800">{pro.rating}</span>
                          {pro.review_count > 0 && (
                            <span className="text-sm text-gray-400">({pro.review_count} reviews)</span>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          {pro.location && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                              <MapPin size={12} />
                              {pro.location}{pro.distance ? ` · ${pro.distance}` : ''}
                            </span>
                          )}
                          {pro.response_time && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                              <Clock size={12} />
                              Responds {String(pro.response_time).toLowerCase()}
                            </span>
                          )}
                          {pro.years_experience && (
                            <span className="text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
                              {pro.years_experience} yrs exp.
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {pro.description && (
                          <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                            {pro.description}
                          </p>
                        )}

                        {/* Expanded details */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 animate-in">
                            <div>
                              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Services offered</h4>
                              <div className="flex flex-wrap gap-1.5">
                                {pro.services.map((svc, i) => (
                                  <span key={i} className="text-xs bg-[#37b34a]/10 text-[#37b34a] px-2.5 py-1 rounded-full">
                                    {svc.replace(/-/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Price range</h4>
                              <p className="text-sm text-gray-700">
                                {pro.price_range === 'R' ? 'Budget-friendly' : pro.price_range === 'RR' ? 'Mid-range' : 'Premium'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setExpandedPro(isExpanded ? null : idx)}
                        className="text-sm text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1 transition-colors"
                      >
                        {isExpanded ? <><ChevronUp size={16} /> Less info</> : <><ChevronDown size={16} /> More info</>}
                      </button>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleContact(pro.id)}
                          disabled={isContacted}
                          className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                            isContacted
                              ? 'bg-gray-100 text-gray-400 cursor-default'
                              : 'bg-[#37b34a] hover:bg-[#2d9a3e] text-white'
                          }`}
                        >
                          {isContacted ? (
                            <><CheckCircle size={14} /> Contacted</>
                          ) : (
                            <><MessageSquare size={14} /> Contact</>
                          )}
                        </button>
                        <button
                          onClick={() => handleContact(pro.id)}
                          className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
                          title="Call"
                        >
                          <Phone size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        {professionals.length > 0 && (
          <div className="mt-8 bg-white rounded-xl p-6 text-center border border-gray-100">
            <p className="text-gray-600 text-sm mb-3">
              Didn&apos;t find what you&apos;re looking for?
            </p>
            <button
              onClick={() => navigate(`/service/${slug}`)}
              className="text-[#37b34a] hover:text-[#2d9a3e] font-semibold text-sm hover:underline transition-colors"
            >
              Submit another request for {serviceName}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;
